import PropTypes from "prop-types";
import { useMemo } from "react";
// @mui
import { alpha, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import Container from "@mui/material/Container";
// hooks
import { useResponsive } from "src/hooks/use-responsive";
import { useLocales } from "src/locales";
// theme
import { bgGradient } from "src/theme/css";
// components
import Carousel, { CarouselDots, useCarousel } from "src/components/carousel";
import { HeaderSimple as Header } from "../_common";

import "./auth-slider.css";

// ----------------------------------------------------------------------
const CONTENT_GUTTER = { pt: { xs: "72px", sm: "80px" }, pb: { xs: "24px", sm: "32px" } };

export default function AuthClassicLayout({ children, image, title }) {
  const { t } = useLocales();

  const theme = useTheme();

  const carousel = useCarousel({
    autoplay: true,
    ...CarouselDots({
      rounded: true,
      sx: { mt: 3, width: "100%", bottom: "10px", position: "absolute" },
    }),
  });

  const upMd = useResponsive("up", "md");

  const carouselData = useMemo(() => {
    const items = [];

    items.push(t("loginPhrase1"));
    items.push(t("loginPhrase2"));
    items.push(t("loginPhrase3"));
    items.push(t("loginPhrase4"));
    items.push(t("loginPhrase5"));
    items.push(t("loginPhrase6"));
    items.push(t("loginPhrase7"));
    items.push(t("loginPhrase8"));

    return items;
  }, [t]);

  const renderContent = <Container>{children}</Container>;

  const renderSection = (
    <Box
      sx={{
        height: "100%",
        ...bgGradient({
          color: alpha(
            theme.palette.background.default,
            theme.palette.mode === "light" ? 0.78 : 0.94
          ),
          imgUrl: "/assets/background/overlay_4.jpg",
        }),
      }}
    >
      <Carousel
        ref={carousel.carouselRef}
        {...carousel.carouselSettings}
      >
        {carouselData.map(item => (
          <CarouselItem
            key={item}
            item={item}
          />
        ))}
      </Carousel>
    </Box>
  );

  return (
    <>
      <Header hideLogo />

      <Grid
        container
        sx={{ minHeight: "100vh" }}
      >
        <Grid md={8}>{upMd && renderSection}</Grid>

        <Grid
          md={4}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            flexGrow: 1,
            ...CONTENT_GUTTER,
          }}
        >
          {renderContent}
        </Grid>
      </Grid>
    </>
  );
}

AuthClassicLayout.propTypes = {
  children: PropTypes.node,
  image: PropTypes.string,
  title: PropTypes.string,
};

function CarouselItem({ item }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      textAlign="center"
      sx={{ height: "100%" }}
    >
      <Typography variant="h3">{item}</Typography>
    </Box>
  );
}

CarouselItem.propTypes = {
  item: PropTypes.string,
};
